<template>
  <div
    class="rsd-item"
    :class="{ 'rsd-item--embedded': embedded }"
  >
    <div
      v-if="!embedded"
      class="rsd-form-page-actions"
    >
      <button
        type="button"
        class="btn page-header-btn page-header-btn--secondary"
        @click="goBack"
      >
        <i
          class="bi bi-arrow-left-short me-1"
          aria-hidden="true"
        />
        {{ t('common.back') }}
      </button>
      <button
        v-if="model.id"
        type="button"
        class="btn page-header-btn page-header-btn--secondary"
        :disabled="exportPending"
        @click="exportPdf"
      >
        <i class="bi bi-file-earmark-pdf me-1" />
        {{ t('actions.exportPdf') }}
      </button>
    </div>

    <Form
      ref="form"
      entity-name="rsd"
      :entity="model"
      :entity-rbac="['rsd_edit']"
      :rules="rules"
      :hide-controls="embedded"
      :sticky-controls="!embedded"
      @form:apply="onSave"
      @form:reset="onFormReset"
      @form:update="Object.assign(model, $event)"
      @form:editable="onFormEditable"
    >
      <template #default="{ editable }">
        <div
          class="rsd-form"
          :class="editable ? 'edit-mode' : 'view-mode'"
        >
          <div class="rsd-form-top-grid">
            <div class="rsd-field">
              <label>{{ t('labels.tradeName') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="tradeNameForLocale"
              >
            </div>
            <div class="rsd-field">
              <label>{{ t('labels.inn') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="innForLocale"
              >
            </div>
            <div class="rsd-field">
              <label>{{ t('labels.moleculeCode') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="readonly.azd_code"
              >
            </div>
            <div class="rsd-field">
              <label>{{ t('labels.dosageForm') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="dosageFormForLocale"
              >
            </div>
            <div class="rsd-field">
              <label>{{ t('labels.dosage') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="dosageForLocale"
              >
            </div>
            <div class="rsd-field">
              <label>{{ t('labels.releaseForm') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="releaseFormForLocale"
              >
            </div>
            <div class="rsd-field rsd-field--full">
              <label>{{ t('labels.indications') }}</label>
              <input
                type="text"
                class="form-control"
                data-registry="1"
                disabled
                :value="indicationsForRegistration"
              >
            </div>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.regulatory') }}</div>
            <table class="rsd-table">
              <thead>
                <tr>
                  <th>{{ t('table.item') }}</th>
                  <th>{{ t('table.description') }}</th>
                </tr>
              </thead>
              <tbody>
                <RsdFieldRow
                  :label="t('labels.registrationType')"
                  comment-key="registration_type"
                  v-model:comment="model.field_comments.registration_type"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.registration_type"
                    class="form-select"
                    :class="rsdSelectClass(model.registration_type)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in registrationTypeOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.registrationProcedure')"
                  comment-key="registration_procedure"
                  v-model:comment="model.field_comments.registration_procedure"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.registration_procedure"
                    class="form-select"
                    :class="rsdSelectClass(model.registration_procedure)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in registrationProcedureOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.referenceState')"
                  comment-key="reference_state"
                  v-model:comment="model.field_comments.reference_state"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.reference_state"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.referenceState')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.recognitionStates')"
                  comment-key="recognition_states"
                  v-model:comment="model.field_comments.recognition_states"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.recognition_states"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.recognitionStates')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.worldRegistrationPeriod')"
                  comment-key="world_registration_period"
                  v-model:comment="model.field_comments.world_registration_period"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.world_registration_period" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.refStateRegistrationPeriod')"
                  comment-key="ref_state_registration_period"
                  v-model:comment="model.field_comments.ref_state_registration_period"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.ref_state_registration_period" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.additionalRequirements')"
                  comment-key="additional_requirements_status"
                  v-model:comment="model.field_comments.additional_requirements_status"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.additional_requirements_status"
                    class="form-select"
                    :class="rsdSelectClass(model.additional_requirements_status)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in applicableFemOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.conditionalRegistration')"
                  comment-key="conditional_registration_status"
                  v-model:comment="model.field_comments.conditional_registration_status"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.conditional_registration_status"
                    class="form-select"
                    :class="rsdSelectClass(model.conditional_registration_status)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in applicableFemOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.acceleratedRegistration')"
                  comment-key="accelerated_registration_status"
                  v-model:comment="model.field_comments.accelerated_registration_status"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.accelerated_registration_status"
                    class="form-select"
                    :class="rsdSelectClass(model.accelerated_registration_status)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in applicableFemOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.exceptionalRegistration')"
                  comment-key="exceptional_registration_status"
                  v-model:comment="model.field_comments.exceptional_registration_status"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.exceptional_registration_status"
                    class="form-select"
                    :class="rsdSelectClass(model.exceptional_registration_status)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in applicableFemOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.nosologyCode')"
                  comment-key="nosology_code"
                  v-model:comment="model.field_comments.nosology_code"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.nosology_code"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.nosologyCode')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.expertConclusion')"
                  comment-key="conclusion"
                  v-model:comment="model.field_comments.conclusion"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="d-flex flex-wrap gap-2 align-items-end">
                    <input
                      v-model="model.conclusion_number"
                      type="text"
                      class="form-control"
                      :placeholder="t('labels.conclusionNumber')"
                    >
                    <DatePicker v-model="model.conclusion_date" />
                    <div class="rsd-inline-date">
                      <label class="form-label mb-1">{{ t('labels.plannedSubmissionApprovalDate') }}</label>
                      <DatePicker v-model="model.planned_submission_approval_date" />
                    </div>
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.regulatorInteraction')"
                  comment-key="regulator_interaction"
                  v-model:comment="model.field_comments.regulator_interaction"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.regulator_interaction"
                    class="form-select"
                    :class="rsdSelectClass(model.regulator_interaction)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in regulatorInteractionOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.clinicalData')"
                  comment-key="clinical_data"
                  v-model:comment="model.field_comments.clinical_data"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.clinical_data"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.riskDescription')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.gmp')"
                  comment-key="gmp_notes"
                  v-model:comment="model.field_comments.gmp_notes"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.gmp_notes"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.riskDescription')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.gcp')"
                  comment-key="gcp"
                  v-model:comment="model.field_comments.gcp"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.gcp"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.riskDescription')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.samples')"
                  comment-key="samples"
                  v-model:comment="model.field_comments.samples"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.samples"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.riskDescription')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.other')"
                  comment-key="other_regulatory"
                  v-model:comment="model.field_comments.other_regulatory"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.other_regulatory"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.otherRisk')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.packagingDescription')"
                  comment-key="packaging_description"
                  v-model:comment="model.field_comments.packaging_description"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    type="text"
                    class="form-control"
                    data-registry="1"
                    disabled
                    :value="releaseFormForLocale"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.skuGtin')"
                  comment-key="sku_gtin"
                  v-model:comment="model.field_comments.sku_gtin"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.sku_gtin"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.skuGtin')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.mah')"
                  comment-key="mah"
                  v-model:comment="model.field_comments.mah"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    type="text"
                    class="form-control"
                    data-registry="1"
                    disabled
                    :value="mahForLocale"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.manufacturingSites')"
                  comment-key="manufacturing_sites"
                  v-model:comment="model.field_comments.manufacturing_sites"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="rsd-contacts-grid">
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.manufacturer') }}</label>
                      <input
                        type="text"
                        class="form-control"
                        data-registry="1"
                        disabled
                        :value="readonly.manufacturer"
                        :placeholder="t('ph.manufacturer')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.primaryPackager') }}</label>
                      <input
                        type="text"
                        class="form-control"
                        data-registry="1"
                        disabled
                        :value="readonly.primary_packager"
                        :placeholder="t('ph.packager')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.secondaryPackager') }}</label>
                      <input
                        type="text"
                        class="form-control"
                        data-registry="1"
                        disabled
                        :value="readonly.secondary_packager"
                        :placeholder="t('ph.packager')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.batchReleaseQc') }}</label>
                      <input
                        type="text"
                        class="form-control"
                        data-registry="1"
                        disabled
                        :value="readonly.batch_release_qc"
                        :placeholder="t('ph.qcSite')"
                      >
                    </div>
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.globalTradeName')"
                  comment-key="global_trade_name"
                  v-model:comment="model.field_comments.global_trade_name"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    type="text"
                    class="form-control"
                    data-registry="1"
                    disabled
                    :value="readonly.global_trade_name || readonly.trade_name_en"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.localTradeName')"
                  comment-key="local_trade_name"
                  v-model:comment="model.field_comments.local_trade_name"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    type="text"
                    class="form-control"
                    data-registry="1"
                    disabled
                    :value="readonly.local_trade_name || readonly.trade_name_ru"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.trademarkProtection')"
                  comment-key="trademark_protection"
                  v-model:comment="model.field_comments.trademark_protection"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.trademark_protection"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.trademarkProtection')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.globalTeamContacts')"
                  comment-key="global_team"
                  v-model:comment="model.field_comments.global_team"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="rsd-contacts-grid">
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.contactIrl') }}</label>
                      <input
                        v-model="model.global_team_irl_alexion_contacts"
                        type="text"
                        class="form-control"
                        :placeholder="t('ph.contact')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.contactIrad') }}</label>
                      <input
                        v-model="model.global_team_irad"
                        type="text"
                        class="form-control"
                        :placeholder="t('ph.contact')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.contactIram') }}</label>
                      <input
                        v-model="model.global_team_iram"
                        type="text"
                        class="form-control"
                        :placeholder="t('ph.contact')"
                      >
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.contactIcmc') }}</label>
                      <input
                        v-model="model.global_team_icmc"
                        type="text"
                        class="form-control"
                        :placeholder="t('ph.contact')"
                      >
                    </div>
                  </div>
                </RsdFieldRow>
              </tbody>
            </table>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.clinical') }}</div>
            <table class="rsd-table">
              <thead>
                <tr>
                  <th>{{ t('table.item') }}</th>
                  <th>{{ t('table.description') }}</th>
                </tr>
              </thead>
              <tbody>
                <RsdFieldRow
                  :label="t('labels.plannedIndication')"
                  comment-key="planned_indication_registration"
                  v-model:comment="model.field_comments.planned_indication_registration"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <textarea
                    v-model="model.planned_indication_registration"
                    class="form-control"
                    rows="3"
                    :placeholder="t('ph.plannedIndication')"
                  />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.studyCodeName')"
                  comment-key="study_code_name"
                  v-model:comment="model.field_comments.study_code_name"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.study_code_name"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.studyCodeName')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.globalIcsr')"
                  comment-key="global_icsr_ready_date"
                  v-model:comment="model.field_comments.global_icsr_ready_date"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.global_icsr_ready_date" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.globalFcsr')"
                  comment-key="global_fcsr_ready_date"
                  v-model:comment="model.field_comments.global_fcsr_ready_date"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.global_fcsr_ready_date" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.rpcApprovalStatus')"
                  comment-key="rpc_approval_status"
                  v-model:comment="model.field_comments.rpc_approval_status"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.rpc_approval_status"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.status')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.studyNamePhase')"
                  comment-key="study_name_phase"
                  v-model:comment="model.field_comments.study_name_phase"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <input
                    v-model="model.study_name_phase"
                    type="text"
                    class="form-control"
                    :placeholder="t('ph.studyNamePhase')"
                  >
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.clinicalTrialPermissionDate')"
                  comment-key="clinical_trial_permission_date"
                  v-model:comment="model.field_comments.clinical_trial_permission_date"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.clinical_trial_permission_date" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.localIcsr')"
                  comment-key="icsr_ready_date"
                  v-model:comment="model.field_comments.icsr_ready_date"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.icsr_ready_date" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.localFcsr')"
                  comment-key="local_fcsr_ready_date"
                  v-model:comment="model.field_comments.local_fcsr_ready_date"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <DatePicker v-model="model.local_fcsr_ready_date" />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.studyIndications')"
                  comment-key="study_indications"
                  v-model:comment="model.field_comments.study_indications"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <textarea
                    v-model="model.study_indications"
                    class="form-control"
                    rows="3"
                    :placeholder="t('ph.studyIndications')"
                  />
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.plannedRegistrationDates')"
                  :detail="false"
                >
                  <div class="rsd-contacts-grid">
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.plannedFirstGlobalSubmissionDate') }}</label>
                      <DatePicker v-model="model.planned_first_global_submission_date" />
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.plannedUsEuApprovalDate') }}</label>
                      <DatePicker v-model="model.planned_us_eu_approval_date" />
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.plannedRfSubmissionDate') }}</label>
                      <DatePicker v-model="model.planned_rf_submission_date" />
                    </div>
                    <div class="rsd-contact-item">
                      <label>{{ t('labels.plannedRfApprovalDate') }}</label>
                      <DatePicker v-model="model.planned_rf_approval_date" />
                    </div>
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.earlyAccessProgram')"
                  comment-key="early_access_program"
                  v-model:comment="model.field_comments.early_access_program"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <select
                    v-model="model.early_access_program"
                    class="form-select"
                    :class="rsdSelectClass(model.early_access_program)"
                    :disabled="!editable"
                  >
                    <option value="">{{ t('options.empty') }}</option>
                    <option
                      v-for="opt in applicableOptions"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </RsdFieldRow>
              </tbody>
            </table>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.commercial') }}</div>
            <table class="rsd-table">
              <thead>
                <tr>
                  <th>{{ t('table.item') }}</th>
                  <th>{{ t('table.description') }}</th>
                </tr>
              </thead>
              <tbody>
                <RsdFieldRow
                  :label="t('labels.localizeSecondaryPackaging')"
                  comment-key="localize_secondary_packaging"
                  v-model:comment="model.field_comments.localize_secondary_packaging"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <select
                      class="form-select"
                      :class="rsdSelectClass(model.localize_secondary_packaging ? 'yes' : 'no')"
                      :value="model.localize_secondary_packaging ? 'yes' : 'no'"
                      :disabled="!editable"
                      @change="model.localize_secondary_packaging = $event.target.value === 'yes'"
                    >
                      <option
                        v-for="opt in yesNoOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <DatePicker
                      v-if="model.localize_secondary_packaging"
                      v-model="model.localization_planned_dates"
                      :disabled="!editable"
                    />
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.localizeFdfProduction')"
                  comment-key="localize_fdf_production"
                  v-model:comment="model.field_comments.localize_fdf_production"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <select
                      class="form-select"
                      :class="rsdSelectClass(model.localize_fdf_production ? 'yes' : 'no')"
                      :value="model.localize_fdf_production ? 'yes' : 'no'"
                      :disabled="!editable"
                      @change="model.localize_fdf_production = $event.target.value === 'yes'"
                    >
                      <option
                        v-for="opt in yesNoOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <DatePicker
                      v-if="model.localize_fdf_production"
                      v-model="model.localization_fdf_planned_dates"
                      :disabled="!editable"
                    />
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.inclusionStrategy')"
                  comment-key="inclusion_strategy"
                  v-model:comment="model.field_comments.inclusion_strategy"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <textarea
                    v-model="model.inclusion_strategy"
                    class="form-control"
                    rows="3"
                  />
                </RsdFieldRow>
              </tbody>
            </table>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.legal') }}</div>
            <table class="rsd-table">
              <tbody>
                <RsdFieldRow
                  :label="t('labels.patentsPresence')"
                  comment-key="patents_text"
                  v-model:comment="model.field_comments.patents_text"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="rsd-patents">
                    <div
                      v-for="(row, index) in model.patents"
                      :key="`patent-${index}`"
                      class="rsd-patents__row d-flex flex-wrap gap-2 align-items-center mb-2"
                    >
                      <input
                        v-model="row.number"
                        type="text"
                        class="form-control"
                        :placeholder="t('ph.patentNumber')"
                        :disabled="!editable"
                      >
                      <DatePicker
                        v-model="row.expiry_date"
                        :disabled="!editable"
                      />
                      <button
                        v-if="editable"
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        :aria-label="t('actions.removePatent')"
                        @click.prevent="removePatentRow(index)"
                      >
                        <i class="bi bi-trash" aria-hidden="true" />
                      </button>
                    </div>
                    <button
                      v-if="editable"
                      type="button"
                      class="btn page-header-btn page-header-btn--ghost"
                      @click.prevent="addPatentRow"
                    >
                      <i class="bi bi-plus" aria-hidden="true" />
                      {{ t('actions.addPatent') }}
                    </button>
                  </div>
                </RsdFieldRow>
              </tbody>
            </table>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.circulation') }}</div>
            <table class="rsd-table">
              <thead>
                <tr>
                  <th>{{ t('table.item') }}</th>
                  <th>{{ t('table.description') }}</th>
                </tr>
              </thead>
              <tbody>
                <RsdFieldRow
                  :label="t('labels.preliminaryTesting')"
                  comment-key="circulation_preliminary_testing"
                  v-model:comment="model.field_comments.circulation_preliminary_testing"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="d-flex flex-wrap gap-2 align-items-center">
                    <select
                      class="form-select"
                      :class="rsdSelectClass(model.circulation_preliminary_testing ? 'yes' : 'no')"
                      :value="model.circulation_preliminary_testing ? 'yes' : 'no'"
                      :disabled="!editable"
                      @change="model.circulation_preliminary_testing = $event.target.value === 'yes'"
                    >
                      <option
                        v-for="opt in yesNoOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                    <DatePicker
                      v-if="model.circulation_preliminary_testing"
                      v-model="model.circulation_preliminary_testing_date"
                      :disabled="!editable"
                    />
                  </div>
                </RsdFieldRow>
                <RsdFieldRow
                  :label="t('labels.launchStrategy')"
                  comment-key="circulation_launch_strategy_text"
                  v-model:comment="model.field_comments.circulation_launch_strategy_text"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <div class="d-flex flex-column gap-2">
                    <div class="d-flex flex-wrap gap-2 align-items-center">
                      <select
                        class="form-select"
                        :class="rsdSelectClass(model.circulation_launch_strategy ? 'yes' : 'no')"
                        :value="model.circulation_launch_strategy ? 'yes' : 'no'"
                        :disabled="!editable"
                        @change="model.circulation_launch_strategy = $event.target.value === 'yes'"
                      >
                        <option
                          v-for="opt in yesNoOptions"
                          :key="opt.value"
                          :value="opt.value"
                        >
                          {{ opt.label }}
                        </option>
                      </select>
                      <DatePicker
                        v-if="model.circulation_launch_strategy"
                        v-model="model.circulation_launch_strategy_date"
                        :disabled="!editable"
                      />
                    </div>
                    <input
                      v-if="model.circulation_launch_strategy"
                      v-model="model.circulation_launch_strategy_text"
                      type="text"
                      class="form-control"
                      :placeholder="t('ph.launchStrategy')"
                      :disabled="!editable"
                    >
                  </div>
                </RsdFieldRow>
              </tbody>
            </table>
          </div>

          <div class="rsd-section">
            <div class="rsd-section-title">{{ t('sections.other') }}</div>
            <table class="rsd-table">
              <thead>
                <tr>
                  <th>{{ t('table.item') }}</th>
                  <th>{{ t('table.description') }}</th>
                </tr>
              </thead>
              <tbody>
                <RsdFieldRow
                  :label="t('labels.otherInformation')"
                  comment-key="other_information"
                  v-model:comment="model.field_comments.other_information"
                  :editable="editable"
                  :comment-title="t('detailComment')"
                  :comment-placeholder="t('detailPlaceholder')"
                >
                  <textarea
                    v-model="model.other_information"
                    class="form-control"
                    rows="3"
                  />
                </RsdFieldRow>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <template #sticky-end>
        <History
          v-if="!embedded && model._loaded && model.id"
          model="Rsd"
          :id="model.id"
        />
      </template>
    </Form>

    <History
      v-if="!embedded && model._loaded && model.id"
      ref="history"
      model="Rsd"
      :id="model.id"
      class="mt-3"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import DatePicker from '@/components/datepicker.vue'
import Form from '@/components/form.vue'
import History from '@/components/history/history.vue'
import RsdFieldRow from '@/views/rsd/RsdFieldRow.vue'
import { useApi } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import { useUiStore } from '@/stores/ui'
import { saveBlob } from '@/lib/download'
import { uiPrefersEnglish } from '@/lib/localizedTitle'
import { localizedPair } from '@/lib/productCard'
import {
  APPLICABLE_KEYS,
  REGISTRATION_PROCEDURE_KEYS,
  REGISTRATION_TYPE_KEYS,
  REGULATOR_INTERACTION_KEYS,
  RSD_COMMENT_KEYS,
  YES_NO_KEYS,
} from './rsdFieldOptions'
import { getRsdFormNumber } from '@/lib/sopsCatalog'

const props = defineProps({
  id: { type: Number, default: null },
  /** Режим внутри модалки: без page chrome, кнопки снаружи */
  embedded: { type: Boolean, default: false },
})

const emit = defineEmits(['saved', 'editable'])

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { push } = useMessageStack()
const { blockScreen } = storeToRefs(useUiStore())
const preferEnglish = computed(() => uiPrefersEnglish(locale.value))

const rsdFormNumber = getRsdFormNumber()
const form = ref(null)
const history = ref(null)
const formEditable = ref(false)
const rules = computed(() => [
  {
    name: 'product',
    type: 'object',
    required: true,
    message: t('validation.productRequired'),
  },
])

function emptyFieldComments() {
  return Object.fromEntries(RSD_COMMENT_KEYS.map(key => [key, '']))
}

function mergeFieldComments(raw) {
  const base = emptyFieldComments()
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  for (const key of RSD_COMMENT_KEYS) {
    if (key in src && src[key] != null) {
      base[key] = String(src[key])
    }
  }
  return base
}

function emptyPatentRow() {
  return { number: '', expiry_date: null }
}

function normalizePatents(raw, patentsText = '') {
  const rows = Array.isArray(raw) ? raw : []
  const normalized = rows
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      number: String(item.number || '').trim(),
      expiry_date: item.expiry_date || null,
    }))
    .filter(item => item.number || item.expiry_date)
  if (normalized.length) {
    return normalized
  }
  const legacy = String(patentsText || '').trim()
  if (legacy) {
    return [{ number: legacy, expiry_date: null }]
  }
  return [emptyPatentRow()]
}

function formatPatentsText(patents) {
  const parts = []
  for (const item of patents) {
    if (!item || typeof item !== 'object') continue
    const number = String(item.number || '').trim()
    const expiry = String(item.expiry_date || '').trim()
    if (number && expiry) {
      parts.push(`${number} (${expiry})`)
    } else if (number) {
      parts.push(number)
    } else if (expiry) {
      parts.push(expiry)
    }
  }
  return parts.join('; ')
}

function serializePatents(patents) {
  return (Array.isArray(patents) ? patents : [])
    .map(item => ({
      number: String(item?.number || '').trim(),
      expiry_date: item?.expiry_date || null,
    }))
    .filter(item => item.number || item.expiry_date)
}

function addPatentRow() {
  if (!Array.isArray(model.patents)) {
    model.patents = []
  }
  model.patents.push(emptyPatentRow())
}

function removePatentRow(index) {
  const next = (model.patents || []).slice()
  next.splice(index, 1)
  model.patents = next.length ? next : [emptyPatentRow()]
}

const model = reactive({
  id: null,
  _loaded: false,
  date_created: null,
  title: '',
  product: null,
  product_readonly: {},
  field_comments: emptyFieldComments(),
  registration_type: '',
  registration_procedure: '',
  post_registration_measures: false,
  post_registration_measures_type: '',
  additional_requirements_status: '',
  conditional_registration_status: '',
  accelerated_registration_status: '',
  exceptional_registration_status: '',
  reference_state: '',
  recognition_states: '',
  packaging_description: '',
  global_trade_name: '',
  local_trade_name: '',
  nosology_code: '',
  planned_submission_approval_date: null,
  conclusion_number: '',
  conclusion_date: null,
  regulator_interaction: '',
  clinical_data: '',
  gcp: '',
  gmp_notes: '',
  samples: '',
  other_regulatory: '',
  sku_gtin: '',
  trademark_protection: '',
  global_team_irad: '',
  global_team_iram: '',
  global_team_icmc: '',
  global_team_irl_alexion_contacts: '',
  world_registration_period: null,
  ref_state_registration_period: null,
  planned_indication_registration: '',
  study_code_name: '',
  study_name_phase: '',
  study_indications: '',
  early_access_program: '',
  planned_rf_approval_date: null,
  icsr_ready_date_if_applicable: null,
  global_icsr_ready_date: null,
  global_fcsr_ready_date: null,
  rpc_approval_status: '',
  clinical_trial_permission_date: null,
  icsr_ready_date: null,
  fcsr_ready_date: null,
  local_fcsr_ready_date: null,
  planned_first_global_submission_date: null,
  planned_us_eu_approval_date: null,
  planned_rf_submission_date: null,
  localize_secondary_packaging: false,
  localize_fdf_production: false,
  localization_planned_dates: null,
  localization_fdf_planned_dates: null,
  inclusion_strategy: '',
  circulation_preliminary_testing: false,
  circulation_preliminary_testing_date: null,
  circulation_launch_strategy: false,
  circulation_launch_strategy_date: null,
  circulation_launch_strategy_text: '',
  other_information: '',
  patents: [emptyPatentRow()],
  patents_text: '',
})

/** Отдельная модель ответа — иначе Object.assign после PUT затирает field_comments / product. */
const saveResponse = reactive({})
const { refresh: saveRsd } = useApi({ model: saveResponse })

const {
  model: exportFile,
  pending: exportPending,
  refresh: fetchRsdExport,
} = useApi({
  url: computed(() => (model.id ? `rsd/${model.id}/export/pdf` : null)),
  immediate: false,
})

const readonly = computed(() => model.product_readonly || {})
/** Показание для регистрации: Short indication по языку UI, через запятую. */
const indicationsForRegistration = computed(() => {
  const pr = readonly.value || {}
  if (preferEnglish.value) {
    return (
      pr.indications_for_registration_en
      || pr.indications_for_registration_ru
      || pr.indications_for_registration
      || ''
    )
  }
  return (
    pr.indications_for_registration_ru
    || pr.indications_for_registration_en
    || pr.indications_for_registration
    || ''
  )
})
/** Readonly EN/RU поля карточки — сторона по языку UI. */
function localePair(en, ru) {
  return localizedPair(en, ru, preferEnglish.value)
}

const tradeNameForLocale = computed(() => {
  const pr = readonly.value || {}
  return localePair(pr.trade_name_en, pr.trade_name_ru)
})

const innForLocale = computed(() => {
  const pr = readonly.value || {}
  return localePair(pr.inn_en, pr.inn_ru)
})

const dosageFormForLocale = computed(() => {
  const pr = readonly.value || {}
  return localePair(pr.dosage_form_en, pr.dosage_form_ru)
})

const dosageForLocale = computed(() => {
  const pr = readonly.value || {}
  return localePair(pr.dosage_en, pr.dosage_ru)
})

const mahForLocale = computed(() => {
  const pr = readonly.value || {}
  return localePair(pr.mah_en, pr.mah_ru)
})

/** Форма выпуска / Pack size — Pack Size ENG или RUS по языку UI. */
const releaseFormForLocale = computed(() => {
  const pr = readonly.value || {}
  return (
    localePair(pr.pack_size_en, pr.pack_size_ru)
    || localePair(pr.release_form_en, pr.release_form_ru)
    || String(model.packaging_description || '').trim()
  )
})
const productId = computed(() => model.product?.id || Number(route.query.product) || null)
/** Заголовок документа при сохранении (не UI-секция). */
const documentTitle = computed(
  () =>
    readonly.value.trade_name_en ||
    readonly.value.trade_name_ru ||
    model.title ||
    t('card.titleNew')
)

function optionList(keys, prefix) {
  return computed(() => keys.map(value => ({
    value,
    label: t(`${prefix}.${value}`),
  })))
}

const registrationTypeOptions = optionList(REGISTRATION_TYPE_KEYS, 'options.registrationType')
const registrationProcedureOptions = optionList(REGISTRATION_PROCEDURE_KEYS, 'options.registrationProcedure')
const applicableOptions = optionList(APPLICABLE_KEYS, 'options.applicable')
const applicableFemOptions = optionList(APPLICABLE_KEYS, 'options.applicableFem')
const yesNoOptions = optionList(YES_NO_KEYS, 'options.yesNo')
const regulatorInteractionOptions = optionList(REGULATOR_INTERACTION_KEYS, 'options.regulatorInteraction')

function rsdSelectClass(value) {
  if (!value) return ''
  return `rsd-select--${String(value).replace(/-/g, '_')}`
}

function anyPostRegistrationApplicable() {
  return [
    model.additional_requirements_status,
    model.conditional_registration_status,
    model.accelerated_registration_status,
    model.exceptional_registration_status,
  ].some(value => value === 'applicable')
}

watch(
  () => model.localize_secondary_packaging,
  enabled => {
    if (!enabled) {
      model.localization_planned_dates = null
    }
  },
)

watch(
  () => model.localize_fdf_production,
  enabled => {
    if (!enabled) {
      model.localization_fdf_planned_dates = null
    }
  },
)

watch(
  () => model.circulation_preliminary_testing,
  enabled => {
    if (!enabled) {
      model.circulation_preliminary_testing_date = null
    }
  },
)

watch(
  () => model.circulation_launch_strategy,
  enabled => {
    if (!enabled) {
      model.circulation_launch_strategy_date = null
      model.circulation_launch_strategy_text = ''
    }
  },
)

function goBack() {
  if (route.query.from === 'home') {
    const product = Number(route.query.product)
    const query = {}
    if (Number.isFinite(product) && product > 0) {
      query.product = String(product)
    }
    if (route.query.context) {
      query.context = String(route.query.context)
    }
    router.push({ name: 'home', query })
    return
  }
  if (productId.value) {
    router.push({ name: 'products.item', params: { id: productId.value } })
    return
  }
  router.push({ name: 'products.list' })
}

const { refresh: loadItem } = useApi({
  url: computed(() => (props.id ? `rsd/${props.id}` : null)),
  immediate: false,
  pick(d) {
    const pr = d.product_readonly || {}
    const patents = normalizePatents(d.patents, d.patents_text)
    const patentsText = formatPatentsText(serializePatents(patents)) || d.patents_text || ''
    const hasLaunch =
      Boolean(d.circulation_launch_strategy)
      || Boolean(d.circulation_launch_strategy_date)
      || Boolean(String(d.circulation_launch_strategy_text || '').trim())
    Object.assign(model, d, {
      _loaded: true,
      patents,
      patents_text: patentsText,
      product_readonly: pr,
      field_comments: mergeFieldComments(d.field_comments),
      reference_state: d.reference_state || pr.reference_state || '',
      recognition_states: d.recognition_states || pr.recognition_states || '',
      global_trade_name: pr.global_trade_name || d.global_trade_name || '',
      local_trade_name: pr.local_trade_name || d.local_trade_name || '',
      circulation_launch_strategy: hasLaunch,
      global_icsr_ready_date: d.global_icsr_ready_date || d.icsr_ready_date_if_applicable || null,
      global_fcsr_ready_date: d.global_fcsr_ready_date || d.fcsr_ready_date || null,
      local_fcsr_ready_date: d.local_fcsr_ready_date || d.fcsr_ready_date || null,
    })
    return d
  },
})

onMounted(async () => {
  if (props.id) {
    await loadItem()
    ensureProductRef()
    return
  }
  const queryProductId = Number(route.query.product)
  if (queryProductId) {
    const { model: rsdModel, refresh } = useApi({
      url: `products/${queryProductId}/rsd`,
      immediate: false,
    })
    await refresh()
    if (rsdModel.value?.id) {
      router.replace({ name: 'rsd.item', params: { id: rsdModel.value.id } })
    }
  } else {
    model._loaded = true
  }
})

function syncFieldCommentsFromDom() {
  if (typeof document === 'undefined') {
    return
  }
  const root = document.querySelector('.rsd-item')
  if (!root) {
    return
  }
  if (!model.field_comments || typeof model.field_comments !== 'object') {
    model.field_comments = emptyFieldComments()
  }
  root.querySelectorAll('[data-rsd-comment-key]').forEach(el => {
    const key = el.getAttribute('data-rsd-comment-key')
    if (!key) {
      return
    }
    model.field_comments[key] = String(el.value ?? '')
  })
}

function resolveProductId() {
  const p = model.product
  if (p != null && typeof p === 'object' && Number(p.id) > 0) {
    return Number(p.id)
  }
  if (typeof p === 'number' && p > 0) {
    return p
  }
  const n = Number(p)
  if (Number.isFinite(n) && n > 0) {
    return n
  }
  const fromReadonly = Number(model.product_readonly?.id)
  if (fromReadonly > 0) {
    return fromReadonly
  }
  const fromComputed = Number(productId.value)
  if (fromComputed > 0) {
    return fromComputed
  }
  return null
}

function ensureProductRef() {
  const id = resolveProductId()
  if (!id) {
    return
  }
  if (model.product?.id === id) {
    return
  }
  model.product = {
    ...(typeof model.product === 'object' && model.product ? model.product : {}),
    id,
  }
}

function strOrEmpty(value) {
  return value == null ? '' : String(value)
}

function buildPayload() {
  syncFieldCommentsFromDom()
  ensureProductRef()
  const patents = serializePatents(model.patents)
  const patentsText = formatPatentsText(patents)
  const packSize = releaseFormForLocale.value || strOrEmpty(model.packaging_description)
  return {
    title: strOrEmpty(model.title) || documentTitle.value,
    product: resolveProductId(),
    registration_type: strOrEmpty(model.registration_type),
    registration_procedure: strOrEmpty(model.registration_procedure),
    post_registration_measures: anyPostRegistrationApplicable(),
    post_registration_measures_type: strOrEmpty(model.post_registration_measures_type),
    additional_requirements_status: strOrEmpty(model.additional_requirements_status),
    conditional_registration_status: strOrEmpty(model.conditional_registration_status),
    accelerated_registration_status: strOrEmpty(model.accelerated_registration_status),
    exceptional_registration_status: strOrEmpty(model.exceptional_registration_status),
    reference_state: strOrEmpty(model.reference_state),
    recognition_states: strOrEmpty(model.recognition_states),
    packaging_description: packSize,
    global_trade_name: strOrEmpty(
      readonly.value.global_trade_name || readonly.value.trade_name_en
    ),
    local_trade_name: strOrEmpty(
      readonly.value.local_trade_name || readonly.value.trade_name_ru
    ),
    nosology_code: strOrEmpty(model.nosology_code),
    planned_submission_approval_date: model.planned_submission_approval_date || null,
    conclusion_number: strOrEmpty(model.conclusion_number),
    conclusion_date: model.conclusion_date || null,
    regulator_interaction: strOrEmpty(model.regulator_interaction),
    clinical_data: strOrEmpty(model.clinical_data),
    gcp: strOrEmpty(model.gcp),
    gmp_notes: strOrEmpty(model.gmp_notes),
    samples: strOrEmpty(model.samples),
    other_regulatory: strOrEmpty(model.other_regulatory),
    sku_gtin: strOrEmpty(model.sku_gtin),
    trademark_protection: strOrEmpty(model.trademark_protection),
    global_team_irad: strOrEmpty(model.global_team_irad),
    global_team_iram: strOrEmpty(model.global_team_iram),
    global_team_icmc: strOrEmpty(model.global_team_icmc),
    global_team_irl_alexion_contacts: strOrEmpty(model.global_team_irl_alexion_contacts),
    world_registration_period: model.world_registration_period || null,
    ref_state_registration_period: model.ref_state_registration_period || null,
    planned_indication_registration: strOrEmpty(model.planned_indication_registration),
    study_code_name: strOrEmpty(model.study_code_name),
    study_name_phase: strOrEmpty(model.study_name_phase),
    study_indications: strOrEmpty(model.study_indications),
    early_access_program: strOrEmpty(model.early_access_program),
    planned_rf_approval_date: strOrEmpty(model.planned_rf_approval_date),
    icsr_ready_date_if_applicable: strOrEmpty(model.icsr_ready_date_if_applicable),
    global_icsr_ready_date: strOrEmpty(model.global_icsr_ready_date),
    global_fcsr_ready_date: strOrEmpty(model.global_fcsr_ready_date),
    rpc_approval_status: strOrEmpty(model.rpc_approval_status),
    clinical_trial_permission_date: strOrEmpty(model.clinical_trial_permission_date),
    icsr_ready_date: strOrEmpty(model.icsr_ready_date),
    fcsr_ready_date: strOrEmpty(model.fcsr_ready_date),
    local_fcsr_ready_date: strOrEmpty(model.local_fcsr_ready_date),
    planned_first_global_submission_date: strOrEmpty(model.planned_first_global_submission_date),
    planned_us_eu_approval_date: strOrEmpty(model.planned_us_eu_approval_date),
    planned_rf_submission_date: strOrEmpty(model.planned_rf_submission_date),
    localize_secondary_packaging: Boolean(model.localize_secondary_packaging),
    localize_fdf_production: Boolean(model.localize_fdf_production),
    localization_planned_dates: model.localization_planned_dates || null,
    localization_fdf_planned_dates: model.localization_fdf_planned_dates || null,
    inclusion_strategy: strOrEmpty(model.inclusion_strategy),
    circulation_preliminary_testing: Boolean(model.circulation_preliminary_testing),
    circulation_preliminary_testing_date: model.circulation_preliminary_testing_date || null,
    circulation_launch_strategy: Boolean(model.circulation_launch_strategy),
    circulation_launch_strategy_date: model.circulation_launch_strategy_date || null,
    circulation_launch_strategy_text: strOrEmpty(model.circulation_launch_strategy_text),
    other_information: strOrEmpty(model.other_information),
    patents,
    patents_text: patentsText,
    field_comments: mergeFieldComments(model.field_comments),
  }
}

function bilingual(en, ru) {
  const e = String(en || '').trim()
  const r = String(ru || '').trim()
  if (e && r && e !== r) {
    return `${e} / ${r}`
  }
  return e || r
}

async function persistRsd() {
  ensureProductRef()
  const payload = buildPayload()
  if (!payload.product) {
    throw new Error(t('validation.productRequired'))
  }
  // Не вызываем ensureLock при уже взятом lock: поздний ответ lock()
  // через watch(lockItem) может вернуть ключ ПОСЛЕ reset и снова включить edit.
  const id = props.id || model.id
  await saveRsd({
    url: id ? `rsd/${id}` : 'rsd',
    method: id ? 'put' : 'post',
    data: payload,
  })
  if (saveResponse.id && !model.id) {
    model.id = saveResponse.id
  }
  model.patents = normalizePatents(payload.patents)
  model.patents_text = payload.patents_text || ''
  model.field_comments = mergeFieldComments(payload.field_comments)
}

async function onSave() {
  blockScreen.value = true
  try {
    await persistRsd()
    push({ type: 'success', message: t('toasts.saved') })
    if (props.embedded) {
      await leaveEditMode()
      emit('saved')
      return
    }
    if (!props.id && model.id) {
      await router.replace({ name: 'rsd.item', params: { id: model.id } })
      return
    }
    await Promise.all([form.value?.reset(), props.id ? history.value?.refresh() : null])
  } catch (err) {
    push({ type: 'error', message: err?.message || t('toasts.saveFailed') })
  } finally {
    blockScreen.value = false
  }
}

function onFormReset() {
  if (props.id) {
    loadItem()
  }
}

async function exportPdf() {
  if (!model.id) {
    return
  }
  try {
    await fetchRsdExport()
  } catch (error) {
    console.error(error)
    push({ type: 'error', message: t('toasts.exportFailed') })
    return
  }
  const bytes = exportFile.value?.bytes
  if (!bytes) {
    push({ type: 'error', message: t('toasts.exportFailed') })
    return
  }
  saveBlob(bytes, t('exportFileName', { id: model.id }), 'pdf')
  exportFile.value = null
}

const isEditing = computed(() => formEditable.value || Boolean(form.value?.isEditing))
const canEdit = computed(() => Boolean(form.value?.canEdit))
const exportBusy = computed(() => Boolean(exportPending.value))
const formNumber = rsdFormNumber

function onFormEditable(editable) {
  formEditable.value = Boolean(editable)
  emit('editable', formEditable.value)
}

async function submit() {
  ensureProductRef()
  if (props.embedded) {
    return saveFromModal()
  }
  await form.value?.submit?.()
}

/** Save из футера модалки — как Form/taxes: PUT → reset (fieldset disabled). */
async function saveFromModal() {
  ensureProductRef()
  if (!resolveProductId()) {
    const msg = t('validation.productRequired')
    push({ type: 'error', message: msg })
    throw new Error(msg)
  }
  if (!isEditing.value) {
    await startEdit()
  }
  if (!isEditing.value) {
    const msg = t('toasts.saveFailed')
    push({ type: 'error', message: msg })
    throw new Error(msg)
  }
  blockScreen.value = true
  try {
    await persistRsd()
    push({ type: 'success', message: t('toasts.saved') })
    await leaveEditMode()
    emit('saved')
    return true
  } catch (err) {
    const msg = err?.message || t('toasts.saveFailed')
    push({ type: 'error', message: msg })
    throw err instanceof Error ? err : new Error(msg)
  } finally {
    blockScreen.value = false
  }
}

/** Снять lock сразу — fieldset/поля завязаны на Form.isEditing (lk), не на локальный флаг. */
async function leaveEditMode() {
  const savedComments = mergeFieldComments(model.field_comments)

  try {
    await form.value?.reset?.()
  } catch {
    /* ignore */
  }
  try {
    form.value?.forgetLock?.()
  } catch {
    /* ignore */
  }

  formEditable.value = false
  emit('editable', false)

  if (props.id) {
    try {
      await loadItem()
      ensureProductRef()
    } catch {
      /* ignore */
    }
  }

  const fromServer = mergeFieldComments(model.field_comments)
  const serverHas = Object.values(fromServer).some(v => String(v || '').trim())
  const localHas = Object.values(savedComments).some(v => String(v || '').trim())
  if (!serverHas && localHas) {
    model.field_comments = savedComments
  }

  // Поздний ответ lock() мог вернуть ключ — снимаем ещё раз.
  try {
    form.value?.forgetLock?.()
  } catch {
    /* ignore */
  }
  formEditable.value = false
  emit('editable', false)
}

const isDeleted = computed(() => {
  const value = model.deleted
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'True'
})

async function startEdit() {
  if (isDeleted.value) {
    return false
  }
  await form.value?.edit?.()
  return isEditing.value
}

async function exitEdit() {
  await leaveEditMode()
}

defineExpose({
  submit,
  saveFromModal,
  startEdit,
  exitEdit,
  exportPdf,
  isEditing,
  isDeleted,
  canEdit,
  exportBusy,
  formNumber,
})
</script>

<i18n>
{
  "ru": {
    "formNumberHint": "Номер Forms (раздел Forms / Local SOPs)",
    "edit": "Редактировать",
    "exitEdit": "Выйти из редактирования",
    "detailComment": "Комментарий / детализация",
    "detailPlaceholder": "Комментарий (детализация)...",
    "ph": {
      "referenceState": "Укажите РГ",
      "recognitionStates": "Укажите ГП",
      "nosologyCode": "Код нозологии",
      "riskDescription": "Описание риска",
      "otherRisk": "Срок годности, ТН и др.",
      "description": "Описание",
      "skuGtin": "Коды с указанием площадки",
      "globalTradeName": "Глобальное ТН",
      "localTradeName": "Локальное ТН",
      "trademarkProtection": "Статус защиты ТЗ",
      "contact": "Contact",
      "plannedIndication": "Редакция показания при регистрации ЛП",
      "studyCodeName": "Код исследования, название",
      "status": "Статус",
      "studyNamePhase": "Название исследования, фаза",
      "studyIndications": "Перечислить показания (если применимо)",
      "manufacturer": "Укажите производителя",
      "packager": "Укажите упаковщика",
      "qcSite": "Укажите площадку",
      "patentNumber": "Номер патента",
      "launchStrategy": "Стратегия по вводу в ГО"
    },
    "card": {
      "titleNew": "Форма по разработке регуляторной стратегии ЛП",
      "createdAt": "Создано {date}"
    },
    "table": {
      "item": "Пункт",
      "description": "Описание"
    },
    "sections": {
      "general": "Общая информация",
      "regulatory": "Регуляторные аспекты",
      "manufacturing": "Взаимодействие с регулятором",
      "clinical": "Показания и клинические исследования",
      "commercial": "Коммерческие аспекты",
      "legal": "Юридические аспекты",
      "circulation": "Ввод в гражданский оборот",
      "other": "Прочая информация"
    },
    "labels": {
      "tradeName": "Торговое наименование",
      "dosageForm": "Лекарственная форма",
      "inn": "МНН",
      "dosage": "Дозировка",
      "moleculeCode": "Код молекулы",
      "releaseForm": "Форма выпуска",
      "indications": "Показание для регистрации",
      "registrationType": "Тип регистрации",
      "registrationProcedure": "Процедура регистрации",
      "referenceState": "Референтное государство (РГ)",
      "recognitionStates": "Государство-признания (ГП)",
      "worldRegistrationPeriod": "Сроки регистрации — В мире",
      "refStateRegistrationPeriod": "Сроки регистрации — В референтном государстве",
      "additionalRequirements": "Регистрация с установлением дополнительных требований",
      "conditionalRegistration": "Условная регистрация",
      "acceleratedRegistration": "Ускоренная регистрация",
      "exceptionalRegistration": "Регистрация в исключительных случаях",
      "nosologyCode": "Орфанный статус — Наличие нозологии в Перечне",
      "expertConclusion": "Орфанный статус — Экспертное заключение",
      "conclusionNumber": "Номер заключения",
      "conclusionDate": "Дата заключения",
      "plannedSubmissionApprovalDate": "Планируемая дата подачи/одобрения",
      "regulatorInteraction": "Взаимодействие с регулятором до подачи досье",
      "clinicalData": "Регуляторные риски — Клинические данные",
      "gmp": "Регуляторные риски — GMP",
      "gcp": "Регуляторные риски — GCP",
      "samples": "Регуляторные риски — Образцы",
      "other": "Регуляторные риски — Прочие",
      "packagingDescription": "Форма выпуска — Описание системы упаковки",
      "skuGtin": "SKU коды, GTIN",
      "mah": "Держатель РУ (сведения о держателе регистрационного удостоверения)",
      "manufacturingSites": "Производственные площадки",
      "globalTeamContacts": "Контакты глобальной команды",
      "plannedRegistrationDates": "Планируемые сроки регистрации новых показаний",
      "manufacturer": "Производитель ГЛФ",
      "batchReleaseQc": "Выпускающий контроль качества",
      "primaryPackager": "Первичный упаковщик",
      "secondaryPackager": "Вторичный упаковщик",
      "globalTradeName": "Торговое наименование — Глобальное ТН",
      "localTradeName": "Торговое наименование — Локальное ТН",
      "trademarkProtection": "Наличие защиты товарного знака",
      "contactIrl": "IRL (Alexion)",
      "contactIrad": "iRAD",
      "contactIram": "iRAM",
      "contactIcmc": "iCMC",
      "contactPlaceholder": "Contact",
      "plannedIndication": "Планируемая редакция показания",
      "studyCodeName": "Опорное глобальное КИ — Код, название",
      "globalIcsr": "Опорное глобальное КИ — Дата готовности iCSR",
      "globalFcsr": "Опорное глобальное КИ — Дата готовности fCSR",
      "rpcApprovalStatus": "Локальное исследование — Статус согласования с RPC",
      "studyNamePhase": "Локальное исследование — Название, фаза",
      "clinicalTrialPermissionDate": "Локальное исследование — Дата разрешения ЛКИ",
      "localIcsr": "Локальное исследование — Дата готовности iCSR",
      "localFcsr": "Локальное исследование — Дата готовности fCSR",
      "studyIndications": "Расширение показаний +1 год",
      "earlyAccessProgram": "Программа раннего доступа",
      "plannedFirstGlobalSubmissionDate": "Планируемая дата 1-й глобальной подачи",
      "plannedUsEuApprovalDate": "Планируемая дата одобрения США, ЕС (если доступна)",
      "plannedRfSubmissionDate": "Планируемая дата подачи РФ",
      "plannedRfApprovalDate": "Планируемая дата одобрения РФ",
      "localizeSecondaryPackaging": "Локализация вторичной упаковки и ВКК",
      "localizeFdfProduction": "Локализация производства ГЛФ",
      "inclusionStrategy": "Стратегия по включению в EDL / Перечни",
      "patentsPresence": "Наличие патентов, срок действия",
      "preliminaryTesting": "Предварительное тестирование",
      "launchStrategy": "Стратегия по вводу в ГО в рамках лонча",
      "otherInformation": "Прочая важная информация"
    },
    "options": {
      "empty": "— Выберите —",
      "registrationType": {
        "new_registration": "Новая регистрация",
        "registration_extension": "Расширение регистрации"
      },
      "registrationProcedure": {
        "national": "Национальная",
        "eaeu_mrp": "Взаимное признание",
        "eaeu_dcp": "Децентрализованная"
      },
      "regulatorInteraction": {
        "letters": "Официальные письма",
        "consultations": "Предварительные консультации"
      },
      "applicable": {
        "applicable": "Применимо",
        "not_applicable": "Неприменимо"
      },
      "applicableFem": {
        "applicable": "Применима",
        "not_applicable": "Неприменима"
      },
      "yesNo": {
        "yes": "Да",
        "no": "Нет"
      }
    },
    "actions": {
      "exportPdf": "Выгрузить в PDF",
      "addPatent": "Добавить патент",
      "removePatent": "Удалить патент"
    },
    "validation": {
      "productRequired": "Не указан продукт"
    },
    "toasts": {
      "saved": "Данные сохранены",
      "saveFailed": "Не удалось сохранить",
      "exportFailed": "Не удалось выгрузить документ"
    },
    "exportFileName": "RSD_{id}"
  },
  "en": {
    "formNumberHint": "Forms number (Forms / Local SOPs)",
    "edit": "Edit",
    "exitEdit": "Exit editing",
    "detailComment": "Comment / detail",
    "detailPlaceholder": "Comment (detail)...",
    "ph": {
      "referenceState": "Enter RMS",
      "recognitionStates": "Enter CMS",
      "nosologyCode": "Nosology code",
      "riskDescription": "Risk description",
      "otherRisk": "Shelf life, trade name, etc.",
      "description": "Description",
      "skuGtin": "Codes with site",
      "globalTradeName": "Global trade name",
      "localTradeName": "Local trade name",
      "trademarkProtection": "Trademark protection status",
      "contact": "Contact",
      "plannedIndication": "Indication wording for registration",
      "studyCodeName": "Study code, name",
      "status": "Status",
      "studyNamePhase": "Study name, phase",
      "studyIndications": "List indications (if applicable)",
      "manufacturer": "Enter manufacturer",
      "packager": "Enter packager",
      "qcSite": "Enter site",
      "patentNumber": "Patent number",
      "launchStrategy": "Market launch strategy"
    },
    "card": {
      "titleNew": "Regulatory strategy development form",
      "createdAt": "Created {date}"
    },
    "table": {
      "item": "Item",
      "description": "Description"
    },
    "sections": {
      "general": "General information",
      "regulatory": "Regulatory aspects",
      "manufacturing": "Regulator interaction",
      "clinical": "Indications and clinical studies",
      "commercial": "Commercial aspects",
      "legal": "Legal aspects",
      "circulation": "Market launch",
      "other": "Other information"
    },
    "labels": {
      "tradeName": "Trade name",
      "dosageForm": "Dosage form",
      "inn": "INN",
      "dosage": "Dosage",
      "moleculeCode": "Molecule code",
      "releaseForm": "Packaging / release form",
      "indications": "Indication for registration",
      "registrationType": "Registration type",
      "registrationProcedure": "Registration procedure",
      "referenceState": "Reference member state (RMS)",
      "recognitionStates": "Concerned member states (CMS)",
      "worldRegistrationPeriod": "Registration timeline — Global",
      "refStateRegistrationPeriod": "Registration timeline — Reference state",
      "additionalRequirements": "Registration with additional requirements",
      "conditionalRegistration": "Conditional registration",
      "acceleratedRegistration": "Accelerated registration",
      "exceptionalRegistration": "Registration in exceptional cases",
      "nosologyCode": "Orphan status — Nosology in the List",
      "expertConclusion": "Orphan status — Expert conclusion",
      "conclusionNumber": "Assessment report number",
      "conclusionDate": "Assessment report date",
      "plannedSubmissionApprovalDate": "Planned submission/approval date",
      "regulatorInteraction": "Regulator interaction before dossier submission",
      "clinicalData": "Regulatory risks — Clinical data",
      "gmp": "Regulatory risks — GMP",
      "gcp": "Regulatory risks — GCP",
      "samples": "Regulatory risks — Samples",
      "other": "Regulatory risks — Other",
      "packagingDescription": "Release form — Packaging system description",
      "skuGtin": "SKU codes, GTIN",
      "mah": "Marketing authorisation holder",
      "manufacturingSites": "Manufacturing sites",
      "globalTeamContacts": "Global team contacts",
      "plannedRegistrationDates": "Planned registration timelines for new indications",
      "manufacturer": "Finished dosage form manufacturer",
      "batchReleaseQc": "Batch release / QC site",
      "primaryPackager": "Primary packager",
      "secondaryPackager": "Secondary packager",
      "globalTradeName": "Trade name — Global",
      "localTradeName": "Trade name — Local",
      "trademarkProtection": "Trademark protection",
      "contactIrl": "IRL (Alexion)",
      "contactIrad": "iRAD",
      "contactIram": "iRAM",
      "contactIcmc": "iCMC",
      "contactPlaceholder": "Contact",
      "plannedIndication": "Planned indication wording",
      "studyCodeName": "Pivotal global CT — Code, name",
      "globalIcsr": "Pivotal global CT — iCSR ready date",
      "globalFcsr": "Pivotal global CT — fCSR ready date",
      "rpcApprovalStatus": "Local study — RPC approval status",
      "studyNamePhase": "Local study — Name, phase",
      "clinicalTrialPermissionDate": "Local study — Clinical trial permission date",
      "localIcsr": "Local study — iCSR ready date",
      "localFcsr": "Local study — fCSR ready date",
      "studyIndications": "Indication expansion +1 year",
      "earlyAccessProgram": "Early access program",
      "plannedFirstGlobalSubmissionDate": "Planned date of 1st global submission",
      "plannedUsEuApprovalDate": "Planned US/EU approval date (if available)",
      "plannedRfSubmissionDate": "Planned RF submission date",
      "plannedRfApprovalDate": "Planned RF approval date",
      "localizeSecondaryPackaging": "Localise secondary packaging and batch release",
      "localizeFdfProduction": "Localise finished dosage form manufacturing",
      "inclusionStrategy": "Strategy for inclusion in EDL / Lists",
      "patentsPresence": "Patents present, validity period",
      "preliminaryTesting": "Preliminary testing",
      "launchStrategy": "Market launch strategy and timeline",
      "otherInformation": "Other important information"
    },
    "options": {
      "empty": "— Select —",
      "registrationType": {
        "new_registration": "New registration",
        "registration_extension": "Registration extension"
      },
      "registrationProcedure": {
        "national": "National",
        "eaeu_mrp": "Mutual recognition",
        "eaeu_dcp": "Decentralised"
      },
      "regulatorInteraction": {
        "letters": "Official letters",
        "consultations": "Preliminary consultations"
      },
      "applicable": {
        "applicable": "Applicable",
        "not_applicable": "Not applicable"
      },
      "applicableFem": {
        "applicable": "Applicable",
        "not_applicable": "Not applicable"
      },
      "yesNo": {
        "yes": "Yes",
        "no": "No"
      }
    },
    "actions": {
      "exportPdf": "Export to PDF",
      "addPatent": "Add patent",
      "removePatent": "Remove patent"
    },
    "validation": {
      "productRequired": "Product is not specified"
    },
    "toasts": {
      "saved": "The data has been saved",
      "saveFailed": "Failed to save",
      "exportFailed": "Failed to export document"
    },
    "exportFileName": "RSD_{id}"
  }
}
</i18n>
